import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack/Stack'
import Typography from '@mui/material/Typography'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { BackContinue } from '@components/back_continue/back_continue'
import { useBookingContext } from '@providers/booking/booking_context'
import { isUserLoggedIn } from '@providers/session/helpers'
import { useSessionContext } from '@providers/session/session_context'
import { buildPetGroups } from '@shared/booking_groups'
import { MAX_WIDGET_SIZE, STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { i18WithParams as t } from '@shared/locale'
import { Grooms } from '@views/search_pet_addons/grooming/grooms'
import { useRoutes } from 'app/use_routes'
import { GroomingIcon } from 'components-ui/src/asset/grooming_icon'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { useClientBookingSearchByDays } from 'service-api/src/hooks/use_client_booking_search'
import { TBookingSearchOptions } from 'service-api/src/types/general'
import { SearchOffersMembershipAlert } from './search_offers_membership_alert'

export const SearchOffersGrooming = () => {
  const { accountName: locationName = '', serviceTypeName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const isGroomingStandAlone = bookingCtx.state.serviceTypeName === 'grooming'

  const grooms = useClientBookingSearchByDays(locationName, 'grooming', {
    petGroups: buildPetGroups(bookingCtx.state, false, true),
    offersType: 'PRIMARY',
  } as TBookingSearchOptions)

  const loginCheck = async () => {
    if (!isUserLoggedIn(sessionCtx.state.user)) {
      navigateRoute('loginCheck', { locationName, serviceTypeName })
      return false
    }
    return true
  }

  // redirect to home if the state has invalid data
  useRedirectNoData()
  useEffect(() => {
    if (bookingCtx.state.currentState !== 'searchResultsGrooming') {
      bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'searchResultsGrooming' } })
      bookingCtx.dispatch({ type: 'RESET_GROOM_WIZARD_STATE' })
    }
  })

  useEffect(() => {
    if (grooms.isSuccess) {
      bookingCtx.dispatch({
        type: 'LOAD_PET_ADDONS',
        payload: {
          value: [],
          grooms: grooms.data.results,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grooms.data])

  const offerSelected = isGroomingStandAlone
    ? (((bookingCtx.state.cart.groups[0]?.grooms?.length ?? 0) > 0 &&
        bookingCtx.state.cart.groups[0]?.grooms?.every((groom) => groom.offers?.length > 0)) ??
      false)
    : (bookingCtx.state.cart.groups[0]?.grooms?.some((groom) => groom.offers?.length > 0) ?? false)

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Stack direction="row" alignItems="center">
        <GroomingIcon color="primary" sx={{ width: 40, height: 40 }} />
        <Typography variant="h2">{t('searchResults.title.grooming')}</Typography>
      </Stack>
      <Spacer height={3} />
      <SearchOffersMembershipAlert
        hasMembershipsService={!!bookingCtx.state.data.membershipService}
        searchResults={grooms?.data?.results}
        isOvernight
      />
      {grooms.isLoading ? (
        <Skeleton width="100%" variant="rounded" height={200} />
      ) : (
        <Grooms currentTabIndex={0} showTitle={false} />
      )}

      <Spacer height={STICKY_FOOTER_HEIGHT} />
      <BackContinue
        onBack={() => navigateRoute('searchServiceType', { locationName, serviceTypeName })}
        continueEnabled={offerSelected}
        onContinue={loginCheck}
      />
    </Box>
  )
}
